interface KnownAuditNoise {
  pattern: RegExp;
  reason: string;
}

/**
 * NG0100s that originate inside Angular Material (not app code) and only surface under
 * the audit build's `provideCheckNoChangesConfig({ exhaustive: true })`. Material updates
 * some bindings inside its own lifecycle hooks; exhaustive verification (which re-checks
 * every view as if Default) flags them. They are prod-safe (production never runs
 * exhaustive checkNoChanges) and non-blanking.
 *
 * Each entry is matched against the console.error message — keep patterns specific to the
 * framework signature so a genuine app-level NG0100 in the same component still surfaces.
 */
const KNOWN_AUDIT_NOISE: KnownAuditNoise[] = [
  {
    // Material dynamic/lazy content (e.g. mat-tab `matTabContent`) assigns the tab-body
    // `[attr.id]` in `ngAfterContentChecked`. The "created in a change detection hook"
    // hint is unique to this framework class — app-level NG0100s never carry it.
    pattern: /NG0100[\s\S]*(view has been created after its parent|created in a change detection hook)/,
    reason: 'Material dynamic lazy content (mat-tab matTabContent attr.id) — angular/components #28379, #12197',
  },
  {
    pattern: /NG0100[\s\S]*mat-menu-panel-animating/,
    reason: 'MatMenu animation flag — angular/components #31933',
  },
];

/**
 * Audit-build only. The exhaustive `checkNoChanges` interval reports framework-internal
 * NG0100s straight to `console.error` — they do NOT route through the injectable
 * `ErrorHandler` (they fire from an internal timer, outside the application tick). Wrap
 * `console.error` so known, prod-safe framework NG0100s are demoted to a single
 * `console.debug`, keeping the audit console focused on our own missed-notify regressions.
 * Does not change application behaviour — only what the audit logs.
 */
export function installZonelessAuditConsoleFilter(): void {
  const originalError = console.error.bind(console);
  const loggedReasons = new Set<string>();
  console.error = (...args: unknown[]): void => {
    const message = args.map((arg) => (arg instanceof Error ? arg.message : String(arg))).join(' ');
    const known = KNOWN_AUDIT_NOISE.find((entry) => entry.pattern.test(message));
    if (known) {
      if (!loggedReasons.has(known.reason)) {
        loggedReasons.add(known.reason);
        console.debug(`[zoneless-audit] suppressing known framework NG0100: ${known.reason}`);
      }
      return;
    }
    originalError(...args);
  };
}
